import * as React from 'react';
import { useState } from 'react';
import {
  Avatar,
  CssBaseline,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
  Grid,
  Box,
  Typography,
  Container,
  Paper,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { setUser, setSessionExpired } from '../../store/loginUserSlice';
import { setId, setUserNumber, setToken } from '../../store/setupUserSlice';
import Copyright from '../molecules/Copyright';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import { v2api } from '../../api/axios';
import Log from '../../lib/log';
import { useAnalytics } from '../../App';

// ログの初期化
const log = new Log('Login');

// フォームの型
interface LoginFormInput {
  number: number;
  password: number;
  remember: boolean;
}

// バリデーションルール
const schema = yup.object({
  number: yup
    .string()
    .matches(/^0[0-9]{9,10}$/, '電話番号形式が不正です。')
    .required('必須項目です'),
  password: yup
    .string()
    .matches(/^[0-9]{4}$/, 'パスワードは数字4桁です。')
    .required('必須項目です'),
});

export default function Login() {
  log.putLog('info', 'Login started.');
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInput>({
    resolver: yupResolver(schema),
  });
  const navigate = useNavigate();
  const analytics = useAnalytics();

  const [authDisable, setAuthDisable] = useState(true);

  // Cookies
  const [cookies, setCookie, removeCookie] = useCookies(['number']);

  // Remember
  const [checkRemember, setCheckRemember] = useState(
    cookies.number ? true : false,
  );
  const handleChangeRemember = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCheckRemember(e.target.checked);
  };

  // エラーメッセージ
  const [errorMessage, setErrorMessage] = useState('');

  // ボタン表示
  const [buttonDisable, setButtonDisable] = useState(false);

  // フォーム送信時の処理
  const onSubmit: SubmitHandler<LoginFormInput> = async (data) => {
    setButtonDisable(true);
    setAuthDisable(true);

    // ストアを初期化
    dispatch(setUser(''));

    // ログインを確認する
    const postData = {
      Number: data.number,
      Password: data.password,
    };
    const res = await v2api.post('/login', postData);
    switch (res.data.statusCode) {
      case 200: // ログイン成功
        const user = JSON.parse(res.data.body);
        if (data.remember) {
          // Cookieに電話番号を記憶
          setCookie('number', data.number);
        } else {
          removeCookie('number');
        }
        // Segmentに記録
        analytics.identify(user.id, {
          name: user.user_name,
          user_number: user.user_number,
        });
        analytics.track('login', {
          event: 'login success.',
        });
        // セッション有効期限をストアに格納
        const sessionExpired = new Date();
        sessionExpired.setHours(sessionExpired.getHours() + 24); // セッション有効期限はログインしてから24時間
        dispatch(setSessionExpired(sessionExpired.getTime()));
        if (user.user_status === 0) {
          // セットアップユーザ
          dispatch(setId(user.id));
          dispatch(setUserNumber(user.user_number));
          dispatch(setToken(user.access_token));
          return navigate('/terms');
        } else {
          // ストアにuserを格納
          dispatch(setUser(user));
          return navigate('/');
        }
      case 401: // Password error.
        setErrorMessage('パスワードが違います。');
        // Segmentに記録
        analytics.track('login', {
          event: 'login failed.(Invalid password)',
        });
        break;
      case 404: // Not found.
        setErrorMessage('登録されていません。');
        // Segmentに記録
        analytics.track('login', {
          event: 'login failed.(User not found)',
        });
        break;
      case 407: // Token expired.
        setErrorMessage('追加の認証が必要です。');
        setAuthDisable(false);
        // Segmentに記録
        analytics.track('login', {
          event: 'Authentication request.',
        });
        break;
      case 408: // Retry expired.
        setErrorMessage(
          'アカウントがロックされました。しばらくしてからお試しください。',
        );
        // Segmentに記録
        analytics.track('login', {
          event: 'login failed.(Account locked.)',
        });
        break;
      default:
        setErrorMessage('予期せぬエラーが発生しました。');
        // Segmentに記録
        analytics.track('login', {
          event: 'login failed.(Unknown error.)',
        });
        break;
    }
    setButtonDisable(false);
  };

  return (
    <Container component='main' maxWidth='xs'>
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component='h1' variant='h5'>
          ログイン
        </Typography>
        <Box
          component='form'
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{ mt: 1 }}
        >
          <TextField
            margin='normal'
            fullWidth
            id='number'
            label='契約電話番号'
            defaultValue={cookies.number}
            autoComplete='off'
            autoFocus
            {...register('number')}
            error={'number' in errors}
            helperText={errors.number?.message}
          />
          <TextField
            margin='normal'
            fullWidth
            // name='password'
            label='パスワード'
            type='password'
            id='password'
            // autoComplete='current-password'
            {...register('password')}
            error={'password' in errors}
            helperText={errors.password?.message}
          />
          <FormControlLabel
            control={
              <Checkbox
                value='remember'
                color='primary'
                checked={checkRemember}
                onChange={(e) => handleChangeRemember(e)}
              />
            }
            label='契約電話番号を記憶'
            {...register('remember')}
          />
          <Typography variant='h5' color='red'>
            {errorMessage}
          </Typography>
          {!authDisable ? (
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                height: 170,
              }}
            >
              <Typography>
                ご契約いただきました電話番号から
                <Typography color='blue'>
                  <a href={'tel:' + process.env.REACT_APP_AUTH_NUMBER}>
                    {process.env.REACT_APP_AUTH_NUMBER}
                  </a>
                </Typography>
                に電話をかけて、追加の認証を行ってください。
                <br />
                認証が完了しましたら、再度ログインをお願いします。
              </Typography>
            </Paper>
          ) : (
            ''
          )}
          <LoadingButton
            type='submit'
            fullWidth
            variant='contained'
            sx={{ mt: 3, mb: 2 }}
            disabled={buttonDisable}
            loading={buttonDisable}
          >
            ログイン
          </LoadingButton>
          <Grid container>
            <Grid item xs>
              <Link
                href='https://transrec.zendesk.com/hc/ja/articles/201233629'
                variant='body2'
              >
                {'パスワードを忘れた方'}
              </Link>
            </Grid>
            <Grid item>
              <Link href='https://www.transrec.net/' variant='body2'>
                {'アカウントの新規作成'}
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Copyright sx={{ mt: 8, mb: 4 }} />
    </Container>
  );
}
